/**
 * A conversation opened beside a Rule42 record, as a framed Chat42 asks for it.
 *
 * Rule42's panel sends what is on screen as entry context — `kind`, `id`, `code`,
 * `title` and a few more, flat strings — and asks for a session. This turns that
 * into the session's create input, and decides nothing else.
 *
 * **What it carries:** the title the host chose, the entry context as `about`, so
 * the conversation knows from its first turn which record it is beside, and the
 * record's job as a toolset focus, so the tools that read it are in front of the
 * model rather than among the ones a search finds. Where the record names a
 * repository, `openSessionAboutRecord` creates the session with that code
 * attached — and without it, saying why, when it cannot be — and
 * `attachRecordRepository` gives it to a session that already existed without it, or keeps the
 * record's Read-only setting in one that already has it.
 *
 * **A record may link several repositories, one of them primary** (ADR 0087). The host keeps
 * sending `repository` and `repositoryReadOnly` for the primary, exactly as before, and adds
 * `repositories` — a JSON list of `{ url, label?, readOnly, primary }` — only when there is more
 * than one. So a record with one repository takes exactly the path it always took, and a record
 * with several attaches each that it can, the primary first, every one with its own Read-only.
 *
 * **What it never carries is a work item.** In Rule42's profile a work item is a
 * plan to execute: binding one pins that plan's directive, makes the session its
 * execution and tells @pm when it closes. A PRD's panel bound the unit's id that
 * way and failed, because a unit has no directive; a plan's panel would have bound
 * correctly and been worse, because opening a chat beside a plan would have
 * started carrying it out. Removed on 2026-10-14 and kept out by
 * `record-conversation.test.ts`. See ADR 0075.
 *
 * @module chat42/react/rule42/record-conversation
 */

import { FOLDER, sameFolder, sameRepository } from '../repository-names.ts';
import { isProject, projectAbout, projectWorkFor, type ProjectWork } from './project-scope.ts';

/**
 * The Rule42 toolsets a conversation about each kind of record is focused on.
 *
 * A unit of work is read and written in PRD Studio, whose toolset carries the
 * unit and its documents. A plan is read where plans are made, which carries the
 * plan, its unit and the unit's documents. Not `execute`, deliberately: that is
 * the toolset that starts an execution, and a chat beside a plan is not one.
 * Every other Rule42 tool is still a search away; a focus offers, it never
 * removes (ADR 0049). The names are Rule42's, checked against the contract both
 * repositories hold.
 */
export const RECORD_TOOLSETS: Readonly<Record<string, readonly string[]>> = {
  uow: ['prd'],
  atp: ['plan'],
  // A workspace's ideas, managed as a list and one at a time (ADR 0097). `workspace` carries
  // every tool the job uses: tcs_uow_list, tcs_uow_get, tcs_uow_upsert and tcs_planning (read
  // from rule42-cloud's tool definitions on 2026-10-18). `prd` has carried them all too since
  // that evening, when tcs_planning joined it (rule42 7d97f9d2f), so it is no longer the reason;
  // `workspace` stays because it is the smaller set, fifteen tools to prd's twenty-five, and
  // has none of PRD Studio's session tools, which an ideas conversation has no use for.
  // `governance` lacks the unit tools.
  ideas: ['workspace'],
  // A project (ADR 0112): its code, its analysis and its policies. `prd` is the one toolset
  // holding tcs_code, tcs_project_list and tcs_project_upsert together, with five of the six
  // governance tools (read from rule42-cloud's tool definitions on 2026-10-20); `governance`
  // adds the sixth, tcs_governance_delete, and the agents. `plan` holds the three as well, but
  // with the ATP tools a project's conversation has no use for, and `code` holds tcs_code
  // beside the write to Chat42's own index, which it must not reach for.
  project: ['prd', 'governance'],
  // Arthur's general conversation (Rule42's Plan 16, ADR 0141): one per person per organisation, for
  // every screen that has no record of its own (home, settings, administration, a list). `workspace`
  // for the reason it is Ideas': it carries the unit tools and the planning tool, which answer "what
  // is waiting on me" and "what is this screen", in fifteen tools rather than PRD Studio's twenty-five.
  // Every other tool is still a search away; a focus offers, it never removes (ADR 0049).
  arthur: ['workspace'],
};

/**
 * A unit's delivery rather than its PRD: what its plans are, which are ready to run and which
 * are blocked (ADR 0097). Rule42 says so with `scope: 'delivery'` on a unit's entry context, so
 * the record is the same unit and the job is a different one. Focused where plans are read, and
 * not where they run: a chat beside a delivery is not its execution any more than a chat beside
 * a plan is (ADR 0075), and running one is a button the person presses, which widens the focus
 * then (`focusForWork`).
 */
const DELIVERY_FOCUS: readonly string[] = ['plan'];

/** What the conversation is told a delivery session is for, in place of the host's one word. */
export const DELIVERY_SCOPE = 'the delivery of this unit: its ATPs, which are ready to run and which are blocked. '
  + 'Not its PRD, which has a conversation of its own.';

/**
 * What the conversation is told an ideas session is for. Under the runtime's 300 characters for
 * one fact (`aboutOf`), or its end would be cut.
 *
 * Said as what this conversation is, because `about` is context and the runtime's heading says
 * so; the way changes are made here is part of what it is. The owner asked for the AI to be able
 * to make the changes, and this is the care that goes with that: the ones that are many at once
 * or cannot be taken back are asked about first, and every change is said.
 */
export const IDEAS_SCOPE = "idea management for this organisation, through Rule42's tools: the whole list and any "
  + 'one idea. A change to many ideas at once, or one that cannot be taken back (a decline, a merge, a promotion), '
  + 'is put to the person before it is made, and every change made is said afterwards.';

/**
 * What the conversation is told Arthur's general conversation is for (ADR 0141). Under the runtime's
 * 300 characters for one fact. Where the person is in the product is not here: it changes with every
 * screen, so it goes as the location, which takes no turn and leaves this cached "about" alone.
 */
export const ARTHUR_SCOPE = "the person's general conversation with Arthur in this organisation, from any screen of Rule42 "
  + 'that has no conversation of its own. Where they are now is told with each turn. Read what they ask about '
  + "through Rule42's tools; change nothing they did not ask for, and say every change made.";

/** Which of Rule42's scopes an entry context names, by what it carries. */
const isDelivery = (entry: Readonly<Record<string, string>>): boolean =>
  entry['kind'] === 'uow' && entry['scope'] === 'delivery';
const isIdeas = (entry: Readonly<Record<string, string>>): boolean => entry['kind'] === 'ideas';
/** Arthur's general conversation (ADR 0141): about the organisation, with no record and no code. */
const isGeneral = (entry: Readonly<Record<string, string>>): boolean => entry['kind'] === 'arthur';

/**
 * A PRD in a project (Rule42's Plan 05, slice 8; ADR 0136): a unit's own conversation, not its
 * delivery, whose host says its code comes from its project (`codeFrom: "project"`) and names the
 * project. Said in so many words rather than inferred from `projectId`, because an idea in a
 * project sends one too, and an idea is a conversation about the idea, not a PRD.
 *
 * Such a PRD is written from what its project knows, across every repository the project holds,
 * and its code is read through the project's tools. So opening its panel no longer clones or
 * attaches the one repository the host names: that made the conversation look, and behave, as if
 * the PRD were about one repository's files. A PRD with no project keeps the repository, because
 * there is no project knowledge to read instead. A delivery keeps it too: its plans run against code.
 */
export const isProjectPrd = (entry: Readonly<Record<string, string>>): boolean =>
  entry['kind'] === 'uow' && !isDelivery(entry) && entry['codeFrom'] === 'project' && Boolean(entry['projectId']?.trim());

/**
 * What a PRD in a project is told it is for, with the one read it starts from. Kept under the
 * runtime's 300 characters for one fact (`aboutOf`) with a unit id of any usual length, so the
 * call is never cut. The same read Rule42's own PRD Studio instruction names (`prd-studio-rules.ts`),
 * so both sides say the same.
 */
export function projectPrdScope(uowId: string): string {
  return `this PRD, written from its project's knowledge. Start with tcs_uow_get {uowId: "${uowId}", view: "knowledge"}, `
    + 'and check each citation with tcs_code op "verify". No repository is attached: read code through the project.';
}

/**
 * The project a host's record says its conversation works from, for the line under the box
 * (`workingWhere`): a PRD in a project, by the name the host gives it where it gives one.
 */
export function workingProjectOf(entry: Readonly<Record<string, string>>): { name?: string } | undefined {
  if (!isProjectPrd(entry)) return undefined;
  const name = entry['projectLabel']?.replace(/\s+/g, ' ').trim();
  return name ? { name } : {};
}

/**
 * The toolsets a host's `start-work.focus` names, as a session's focus: Rule42's, because
 * Rule42 is the host that sends it. Only identifier-like strings; nothing else is a toolset.
 */
export function focusForWork(focus: unknown): Record<string, string[]> | undefined {
  if (!Array.isArray(focus)) return undefined;
  const names = [...new Set(focus.filter((n): n is string => typeof n === 'string' && /^[a-z][a-z0-9_-]{0,31}$/.test(n)))];
  return names.length ? { [RULE42_CONNECTOR]: names } : undefined;
}

/** The connector Rule42's tools come from. Matches the runtime's `RULE42_CONNECTOR`. */
const RULE42_CONNECTOR = 'rule42';

/** One repository a record links, as its host sends it. */
export interface RecordRepository {
  /** A git remote, or an absolute folder on a machine: the forms `repository` has always taken. */
  url: string;
  /** What people call it on the record ("API", "Mobile app"), where somebody named it. */
  label?: string;
  /** Fetch only. True unless the host said otherwise, as for the single field. */
  readOnly: boolean;
  /** The one a session opens first, and the one its plain `repo_*` tools reach. */
  primary: boolean;
}

/** The most a record links, and so the most a conversation about one is given (ADR 0087). */
const MOST_REPOSITORIES = 20;

/** A repository as a sentence about it names it: by its label where it has one. */
const nameOf = (r: Pick<RecordRepository, 'url' | 'label'>): string => r.label || r.url;

/** What `createSession` is asked for. No `workItem`, by construction. */
export interface RecordSessionInput {
  title?: string;
  about?: Record<string, string>;
  toolsets?: Record<string, readonly string[]>;
}

/**
 * What finding a record's repository needs of a client. `Chat42Client` is one; a test's fake is another.
 */
export interface RepositoryClient {
  checkWorkspace(input: { remote: string }): Promise<
    { ok: true; workspaceId: string } | { ok: false; said: string; next: string; case?: string }
  >;
  workspaces(): Promise<{
    workspaces: ReadonlyArray<{
      id: string; kind: string; path?: string; remote?: string; connected?: boolean; lastUsedAt?: string;
    }>;
  }>;
}

/** What `attachRecordRepository` needs of a client. */
export interface RecordReopenClient extends RepositoryClient {
  attachWorkspace(sessionId: string, workspaceId: string, opts?: { readOnly?: boolean }): Promise<void>;
  /** Code beside code a session already has. See `Chat42Client.addWorkspaces`. */
  addWorkspaces(sessionId: string, workspaceIds: string[], opts?: { readOnly?: boolean }): Promise<void>;
  setWorkspaceReadOnly(
    sessionId: string, input: { remote: string; readOnly: boolean },
  ): Promise<{ changed: number; refused?: number }>;
}

/**
 * What `openSessionAboutRecord` needs of a client: a create, and, for a record with several
 * repositories, what a reopen needs too — an attach that was refused all together is made one
 * repository at a time, and a repository that may push is let push after a read-only create.
 */
export interface RecordSessionClient<S extends { id: string; workspace?: unknown }> extends RecordReopenClient {
  createSession(input: RecordSessionInput & {
    workspaceIds?: string[]; readOnly?: boolean; model?: string; origin?: string; replace?: string;
  }): Promise<S & { found?: true }>;
  /** The caller's own session for a host's origin, or undefined. Asked before creating. */
  hostSession?(origin: string): Promise<(S & SessionCode & { model?: string }) | undefined>;
  /** For a session found by its origin, where the host asked for a model. */
  setSessionModel?(sessionId: string, model: string): Promise<unknown>;
  /** For a session found by its origin: what it is about, as the host says it now. See `keepAboutCurrent`. */
  setSessionAbout?(sessionId: string, about: Record<string, string>): Promise<unknown>;
  /** For an ideas session, which makes its changes without a prompt for each (ADR 0097). */
  setSessionPermissionMode?(sessionId: string, mode: 'auto'): Promise<unknown>;
}

/**
 * Which way attaching a repository failed, and which repository, for a host that says it in its
 * own words (ADR 0097). `case` is one of `attachTrouble`'s, as the server named it, `other` where
 * nothing named one; `repository` is the address the record gave, absent where the trouble is
 * not about one repository.
 */
export interface AttachTroubleAt {
  case: string;
  repository?: string;
}

/** One line for the host, and the trouble it is about where it is about attaching code. */
interface Said {
  line: string;
  trouble?: AttachTroubleAt;
}

/** A trouble about one repository: a line, its case, and the record's address for it. */
const about = (repo: Pick<RecordRepository, 'url'>, line: string, kind: string | undefined): Said =>
  ({ line, trouble: { case: kind || 'other', repository: repo.url } });

/**
 * A session as `GET /api/sessions/:id` reports it, as far as which code it has.
 *
 * Code is on the record as a tier and a repository: `repoId` is the workspace a session opened
 * first and `alsoRepoIds` the ones opened beside it, all at `tier` 1 (cloned) or 2 (a folder on a
 * machine). Tier 0 is a conversation with no code, whatever else the record says.
 */
export interface SessionCode {
  id: string;
  state?: string;
  tier?: number;
  repoId?: string;
  alsoRepoIds?: readonly string[];
}

/**
 * States in which a session is finished for good, as the runtime's `isClosed` has them. Not
 * `failed`: a failed turn can be carried on, so its conversation can still use the code.
 */
const CLOSED = new Set(['done', 'abandoned']);

/** One row of the person's workspaces, as far as finding a record's repository among them. */
type WorkspaceRow = Awaited<ReturnType<RepositoryClient['workspaces']>>['workspaces'][number];

/**
 * The machine folder a repository path names, among the workspaces this person has.
 *
 * A `~/…` path matches a folder at the same place under any home directory, because the record
 * was written by a person and the machine reports the path expanded (`sameFolder`). Where several
 * match, a connected machine's comes first, then the one worked in most recently.
 */
function folderFor(path: string, list: ReadonlyArray<WorkspaceRow>): string | undefined {
  const matches = list.filter((w) => w.kind === 'folder' && Boolean(w.path) && sameFolder(path, w.path!));
  matches.sort((a, b) =>
    Number(b.connected !== false) - Number(a.connected !== false)
    || (b.lastUsedAt ?? '').localeCompare(a.lastUsedAt ?? ''));
  return matches[0]?.id;
}

/**
 * A request the server refused before making anything.
 *
 * Both 4xx answers to a create with workspaces leave nothing behind: a missing permission is
 * refused before the session exists, and an attach that fails deletes the session it made (ADR
 * 0051). A 5xx or a lost connection could have left one, and creating again would make a second
 * conversation for the same record, so those are not retried.
 */
function refusedBeforeMaking(err: unknown): err is { status: number; message?: string; body?: Record<string, unknown> } {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

/**
 * Why an attach was refused, in the server's words where it gave some.
 *
 * Both doors that attach answer a refusal with what happened (`said`) and what to do about it
 * (`next`) beside the older `error` an `HttpError` carries as its message, so the sentence is
 * the server's when there is one and the message only when there is not.
 */
function inServerWords(err: unknown): string {
  return serverSaid(err).words;
}

/** The server's words for a refusal, and the case it named where it named one. */
function serverSaid(err: unknown): { words: string; case?: string } {
  const body = (err as { body?: Record<string, unknown> } | null)?.body;
  const kind = typeof body?.['case'] === 'string' ? body['case'] : undefined;
  const message = err instanceof Error ? err.message : (err as { message?: unknown } | null)?.message;
  const said = typeof body?.['said'] === 'string'
    ? body['said']
    : (typeof message === 'string' && message) || 'It could not be attached.';
  const next = typeof body?.['next'] === 'string' ? ` ${body['next']}` : '';
  return { words: `${said}${next}`, ...(kind ? { case: kind } : {}) };
}

/**
 * The repositories a record links, primary first, and what of the list could not be read.
 *
 * **The single field names the primary, exactly as it always has.** `repository` and
 * `repositoryReadOnly` are what every record sends, and a record with one repository sends
 * nothing else; so where they are present they decide the primary and its Read-only, and the
 * list only adds the others, in the record's own order. Trusting the list's own `primary` over
 * them would let a record with one repository take a different path from the one it took
 * yesterday, and the two can only disagree through a mistake in the host, where the field that
 * has always been right is the safer one to believe.
 *
 * **A list that cannot be read costs only itself.** Not JSON, not a list, empty, or holding
 * nothing with an address: the record's primary is attached as if the list had not been sent,
 * and `unread` says the others were not, for the host to show beside any other reason. An entry
 * without an address is left out the same way and counted, and a list longer than a record may
 * hold is cut at the twentieth, said too. Read-only is true unless an entry says `false` in so
 * many words, as the single field is. `listed` says the list was read and used.
 */
export function recordRepositories(
  entry: Readonly<Record<string, string>>,
): { repositories: RecordRepository[]; unread?: string; listed?: true } {
  // Ideas have no code, and their session asks no git host anything (ADR 0097): an entry for
  // them that named a repository would be a mistake in the host, not a reason to clone.
  if (isIdeas(entry)) return { repositories: [] };
  // Nor does Arthur's general conversation: it is about the organisation, not a codebase (ADR 0141).
  if (isGeneral(entry)) return { repositories: [] };
  // A PRD in a project reads its code through the project (ADR 0136): nothing is cloned for it.
  if (isProjectPrd(entry)) return { repositories: [] };
  const repository = entry['repository']?.trim();
  const single: RecordRepository[] = repository
    ? [{ url: repository, readOnly: readOnlyFor(entry), primary: true }]
    : [];
  const raw = entry['repositories'];
  if (raw === undefined) return { repositories: single };

  const cannotRead = `Opened without this record's other repositories: the list of them could not be read.`;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { repositories: single, unread: cannotRead };
  }
  if (!Array.isArray(parsed)) return { repositories: single, unread: cannotRead };

  const listed: RecordRepository[] = [];
  let unreadable = 0;
  for (const item of parsed as unknown[]) {
    const r = item as { url?: unknown; label?: unknown; readOnly?: unknown; primary?: unknown } | null;
    const url = typeof r?.url === 'string' ? r.url.trim() : '';
    if (!url) {
      unreadable += 1;
      continue;
    }
    const label = typeof r!.label === 'string' ? r!.label.replace(/\s+/g, ' ').trim() : '';
    listed.push({ url, ...(label ? { label } : {}), readOnly: r!.readOnly !== false, primary: r!.primary === true });
  }
  if (!listed.length) return { repositories: single, unread: cannotRead };

  const named = listed.find((r) => repository && sameRepository(r.url, repository));
  const primary: RecordRepository = repository
    ? { url: repository, ...(named?.label ? { label: named.label } : {}), readOnly: readOnlyFor(entry), primary: true }
    : { ...(listed.find((r) => r.primary) ?? listed[0]!), primary: true };
  // No repository twice, a remote compared by host and path as the record compares them, so two
  // spellings of one are not attached as two. Where two list entries disagree about pushing,
  // read-only wins: the record said somewhere that it may not, and that is the setting that
  // protects. The single field is the exception, as above: where it names the primary, its
  // Read-only is the primary's.
  const all = [primary];
  for (const r of listed) {
    const same = all.find((a) => sameRepository(a.url, r.url));
    if (!same) all.push({ ...r, primary: false });
    else if (r.readOnly && !(same === primary && repository)) same.readOnly = true;
  }

  const said: string[] = [];
  if (unreadable) {
    said.push(`Opened without ${unreadable} of this record's repositories: `
      + `${unreadable === 1 ? 'its entry' : 'their entries'} could not be read.`);
  }
  const over = all.length - MOST_REPOSITORIES;
  if (over > 0) {
    said.push(`Opened without ${over} of this record's repositories: a conversation is given at most ${MOST_REPOSITORIES}.`);
  }
  return {
    repositories: all.slice(0, MOST_REPOSITORIES), listed: true, ...(said.length ? { unread: said.join('\n') } : {}),
  };
}

/**
 * The workspace a record's repository names, or why there is none.
 *
 * Shared by both doors — a session created about the record and one reopened beside it — so a
 * repository resolves to the same workspace whichever way the panel was opened. A remote is
 * checked, which registers one Chat42 has not seen; a folder is looked up among the person's
 * workspaces and nothing is registered for it. Never throws: a check that could not be asked is
 * a reason like any other, and the reason is a sentence for the host to show, naming the
 * repository by its label where the record gave it one.
 *
 * `rows` gives the person's workspaces, so a caller resolving several folders, or one that has
 * already looked through them, asks Chat42 for the list once rather than once a folder.
 */
async function workspaceForRepository(
  client: RepositoryClient,
  repo: RecordRepository,
  rows: () => Promise<ReadonlyArray<WorkspaceRow>> = async () => (await client.workspaces()).workspaces,
): Promise<{ workspaceId: string } | { problem: Said }> {
  const without = `Opened without ${nameOf(repo)}.`;
  try {
    if (FOLDER.test(repo.url)) {
      const workspaceId = folderFor(repo.url, await rows());
      if (workspaceId) return { workspaceId };
      // `folder-gone`: of the cases, the one that says the folder is not where it was looked for.
      return {
        problem: about(repo, `${without} None of your paired machines lists that folder. `
          + 'Start the Chat42 daemon on the machine that has it, then open this panel again.', 'folder-gone'),
      };
    }
    const check = await client.checkWorkspace({ remote: repo.url });
    if (check.ok) return { workspaceId: check.workspaceId };
    return { problem: about(repo, `${without} ${check.said} ${check.next}`, check.case) };
  } catch (err) {
    return { problem: about(repo, `${without} ${err instanceof Error ? err.message : String(err)}`, 'other') };
  }
}

/** A record's repository and the workspace it resolved to. */
interface Found {
  repo: RecordRepository;
  workspaceId: string;
}

/**
 * Resolve each repository, in the record's order. Asked together, because each remote is a call
 * to its git host, and three asked in turn would be three waits on a panel that is opening.
 */
async function resolveAll(
  client: RepositoryClient,
  repos: readonly RecordRepository[],
  rows?: ReadonlyArray<WorkspaceRow>,
): Promise<{ found: Found[]; problems: Said[] }> {
  // Asked once, and only if a folder needs it. A list that could not be had is each folder's
  // reason, in the same words.
  let asked: Promise<ReadonlyArray<WorkspaceRow>> | undefined;
  const rowsOnce = rows
    ? async () => rows
    : () => (asked ??= client.workspaces().then((w) => w.workspaces));
  const answers = await Promise.all(repos.map((repo) => workspaceForRepository(client, repo, rowsOnce)));
  const found: Found[] = [];
  const problems: Said[] = [];
  answers.forEach((answer, i) => {
    if ('problem' in answer) problems.push(answer.problem);
    else found.push({ repo: repos[i]!, workspaceId: answer.workspaceId });
  });
  return { found, problems };
}

/**
 * Attach resolved repositories one at a time, so that each stands or fails alone.
 *
 * The first goes in by `attachWorkspace` where the session has no code yet, which gives it its
 * place and its plain tool names; every one after it, and every one where it already has code,
 * by `addWorkspaces`, beside what is there. Each is asked with its own Read-only, so nothing has
 * to be changed afterwards. One refused is one line, in the server's words, and the rest are
 * still asked: a repository that cannot be attached never costs the conversation, and neither
 * does it cost the other repositories.
 */
async function attachEach(
  client: RecordReopenClient,
  sessionId: string,
  found: readonly Found[],
  hasCode: boolean,
): Promise<{ attached: string[]; problems: Said[] }> {
  const attached: string[] = [];
  const problems: Said[] = [];
  let held = hasCode;
  for (const { repo, workspaceId } of found) {
    const opts = repo.readOnly ? { readOnly: true } : undefined;
    try {
      if (held) await client.addWorkspaces(sessionId, [workspaceId], opts);
      else await client.attachWorkspace(sessionId, workspaceId, opts);
      held = true;
      attached.push(workspaceId);
    } catch (err) {
      const said = serverSaid(err);
      problems.push(about(repo, `Opened without ${nameOf(repo)}. ${said.words}`, said.case));
    }
  }
  return { attached, problems };
}

/** Lines for the host, as one message: its panel shows one at a time, and a second replaced the first. */
const lines = (said: ReadonlyArray<string | undefined>): string | undefined =>
  said.filter(Boolean).join('\n') || undefined;

/**
 * The lines, and the first trouble among them. First because the record's order is primary
 * first, so where the primary could not be attached that is the one a host's one status line
 * names; the message still carries every line.
 */
const told = (said: ReadonlyArray<Said | undefined>): { unattached?: string; trouble?: AttachTroubleAt } => {
  const kept = said.filter((x): x is Said => Boolean(x?.line));
  const unattached = lines(kept.map((x) => x.line));
  const trouble = kept.find((x) => x.trouble)?.trouble;
  return { ...(unattached ? { unattached } : {}), ...(trouble ? { trouble } : {}) };
};

/** The list that could not be read, as a trouble about attaching code and about no one repository. */
const unreadSaid = (unread: string | undefined): Said | undefined =>
  (unread ? { line: unread, trouble: { case: 'other' } } : undefined);

/**
 * Create the session about a record, with the record's code attached where the host named it.
 *
 * The repository is a git remote or a folder on a paired machine. A remote is checked first —
 * which registers one Chat42 has not seen — and a folder is looked up among the person's
 * workspaces. The create waits for the code to be attached: a clone can take a minute, and a
 * conversation that says it has the code before it does reasons about a repository it cannot read.
 *
 * **Several repositories are attached in the one create**, the primary first so that it opens
 * first and keeps the plain tool names, and the others beside it. Every one is resolved first,
 * and one that cannot be is left out and said, rather than refusing the rest with it.
 *
 * **Read-only for all where any is, then let push one at a time.** The create takes one setting
 * for everything it opens. Opening all of them writable and then narrowing the read-only ones
 * would leave, for every narrowing that failed, a conversation able to push to a repository its
 * record says it may not — the one failure this setting exists to prevent. Opening all read-only
 * and widening the ones the record lets push fails the other way: a conversation that cannot push
 * where it may, which is said in `unsettled` and costs nothing but the push.
 *
 * **A repository that cannot be attached never costs the conversation.** The person opened a
 * panel to talk about a PRD. Code is what makes the talk better, not what makes it possible. So
 * a failed check, a folder nobody's machine has, or a create refused over its workspace each
 * still produce the conversation, without the code, and `unattached` says why, in the server's
 * words where it gave some, for the host to show — one line for each repository it is without.
 * A create of several refused together says nothing about which of them it was refused over, so
 * the conversation is made without code and each is then attached alone, in its own words.
 * `trouble` is the first of those reasons that is about attaching code, by case and repository,
 * for a host that says it in its own words rather than showing the lines (ADR 0097).
 *
 * **With an origin, it is find-or-create** (ADR 0097). The person's own conversation for that
 * scope is asked for first, which costs one request to Chat42 and none to a git host, and where
 * there is one it is opened as a reopened session is, with its record's code attached if it has
 * none, and `found` says so. Only where there is none is anything resolved and made, under the
 * origin; a create that lost a race for it comes back found and is opened the same way. A
 * lookup that fails is not taken for "none": the create asks the same question on the server.
 *
 * **With a model, the session is made on it**, so the first turn runs on the model the person
 * chose and a reopen has nothing to ask. A session found by its origin is put on it too: the
 * host asked for one only because the person had just chosen it.
 *
 * **An ideas session makes its changes without a prompt for each.** The owner asked for the AI
 * to be able to make them; the session is set to Auto when it is made, which lets reversible
 * work through, still stops anything irreversible, and stays under the organisation's floor. A
 * session found again keeps whatever the person has set since.
 */
export async function openSessionAboutRecord<S extends { id: string; workspace?: unknown }>(
  client: RecordSessionClient<S>,
  entry: Readonly<Record<string, string>>,
  title: string,
  opts: { model?: string; origin?: string; replace?: string } = {},
): Promise<OpenedAboutRecord<S>> {
  // Start over (ADR 0137) is never answered with the conversation it replaces: the server finishes
  // that one and makes the new one under the origin, so nothing is looked up first.
  const replace = opts.origin && opts.replace ? opts.replace : undefined;
  if (opts.origin && !replace && client.hostSession) {
    const existing = await client.hostSession(opts.origin).catch(() => undefined);
    if (existing) return adopt(client, existing, entry, opts.model);
  }
  const input: RecordSessionInput & { model?: string; origin?: string; replace?: string } = {
    ...sessionAboutRecord(entry, title),
    ...(opts.model ? { model: opts.model } : {}),
    ...(opts.origin ? { origin: opts.origin } : {}),
    ...(replace ? { replace } : {}),
  };
  const made = await createAboutRecord(client, entry, input);
  if (made.session.found) return adopt(client, made.session, entry, opts.model);
  // Made just now, so it knew of no run: whatever the host names is new to it (ADR 0112).
  // The session's model named in the brief, so the report's header names its real author (ADR 0118).
  const work = projectWorkFor(entry, undefined, true, opts.model ?? (made.session as { model?: string }).model);
  if (work) return { ...made, work };
  if (isIdeas(entry) && client.setSessionPermissionMode) {
    try {
      await client.setSessionPermissionMode(made.session.id, 'auto');
    } catch (err) {
      const unsettled = lines([made.unsettled,
        `This conversation will ask before each change it makes in Rule42: its permission could not be set. ${inServerWords(err)}`]);
      return { ...made, ...(unsettled ? { unsettled } : {}) };
    }
  }
  return made;
}

/** What opening a session about a record did, for the host to be told. See `openSessionAboutRecord`. */
export interface OpenedAboutRecord<S> {
  session: S & { found?: true };
  /** Found by its origin rather than made. */
  found?: true;
  unattached?: string;
  unsettled?: string;
  trouble?: AttachTroubleAt;
  /**
   * Work to start in it, for a project's conversation told of a run or a draft it did not know
   * (ADR 0112): sent as the host's `start-work` would be, once the session is ready. See
   * `projectWorkFor`.
   */
  work?: ProjectWork;
}

/** A session found by its origin, opened as a reopen opens one. */
async function adopt<S extends { id: string; workspace?: unknown }>(
  client: RecordSessionClient<S>,
  session: S & { found?: true },
  entry: Readonly<Record<string, string>>,
  model: string | undefined,
): Promise<OpenedAboutRecord<S>> {
  // Before anything else is asked of it, so a run started on this session is told the record
  // as the host has it now, the organisation included, from its first turn.
  const aboutTold: { sent: boolean; previous?: Record<string, string> } = client.setSessionAbout
    ? await updateAbout({ setSessionAbout: client.setSessionAbout.bind(client) }, session.id, entry)
    : { sent: false };
  const work = aboutTold.sent
    ? projectWorkFor(entry, aboutTold.previous, aboutTold.previous !== undefined, model ?? (session as { model?: string }).model)
    : undefined;
  const reopened = await attachRecordRepository(client, session as unknown as SessionCode, entry);
  if (model && client.setSessionModel && (session as { model?: string }).model !== model) {
    // Not said if refused (a turn running, say): the host is told the model the next turn will
    // use whatever happens here, so its header names the truth either way.
    await client.setSessionModel(session.id, model).catch(() => undefined);
  }
  return {
    session, found: true,
    ...(reopened.unattached ? { unattached: reopened.unattached } : {}),
    ...(reopened.unsettled ? { unsettled: reopened.unsettled } : {}),
    ...(reopened.trouble ? { trouble: reopened.trouble } : {}),
    ...(work ? { work } : {}),
  };
}

/** The create half of `openSessionAboutRecord`: resolve the record's code, then make the session with it. */
async function createAboutRecord<S extends { id: string; workspace?: unknown }>(
  client: RecordSessionClient<S>,
  entry: Readonly<Record<string, string>>,
  input: RecordSessionInput & { model?: string; origin?: string; replace?: string },
): Promise<OpenedAboutRecord<S>> {
  const { repositories, unread } = recordRepositories(entry);
  const said = (session: S & { found?: true }, problems: readonly Said[], unsettled?: readonly string[]) => {
    // Found by its origin, it was not made by this request: what it has is the reopen's to say.
    if (session.found) return { session };
    const notKept = lines(unsettled ?? []);
    return { session, ...told([...problems, unreadSaid(unread)]), ...(notKept ? { unsettled: notKept } : {}) };
  };
  if (!repositories.length) return said(await client.createSession(input), []);

  const { found, problems } = await resolveAll(client, repositories);
  if (!found.length) return said(await client.createSession(input), problems);

  const readOnly = found.some((f) => f.repo.readOnly);
  try {
    const session = await client.createSession({
      ...input, workspaceIds: found.map((f) => f.workspaceId), ...(readOnly ? { readOnly: true } : {}),
    });
    // Asked for with its code and answered without: a server older than creating a session with
    // a workspace ignores the field. The conversation is kept — it is still the one asked for —
    // and the missing code is said rather than assumed.
    if (!session.workspace && !session.found) {
      return said(session, [
        ...problems,
        ...found.map((f) => about(f.repo,
          `Opened without ${nameOf(f.repo)}. This Chat42 did not attach it when the conversation was created.`, 'other')),
      ]);
    }
    if (session.found) return said(session, []);
    const unsettled: string[] = [];
    if (readOnly) {
      for (const f of found.filter((x) => !x.repo.readOnly)) {
        const kept = await keepReadOnly(client, session.id, f.repo, false);
        if (kept.unsettled) unsettled.push(kept.unsettled);
      }
    }
    return said(session, problems, unsettled);
  } catch (err) {
    if (!refusedBeforeMaking(err)) throw err;
    const session = await client.createSession(input);
    if (session.found) return said(session, []);
    if (found.length === 1) {
      const refused = serverSaid(err);
      return said(session, [...problems, about(found[0]!.repo, `Opened without ${nameOf(found[0]!.repo)}. ${refused.words}`, refused.case)]);
    }
    const each = await attachEach(client, session.id, found, false);
    return said(session, [...problems, ...each.problems]);
  }
}

/**
 * Tell a session that already exists what it is about, as the host says it now (ADR 0101).
 *
 * A session's about used to be set when it was made and never again, so a conversation found
 * by its origin or reopened by its stored id kept the record as it was described the day it was
 * opened. When rule42-cloud began naming the organisation a PRD belongs to, every existing PRD
 * conversation would have gone on without it, in whichever organisation the person's sign-in
 * was issued for. So the about built from the current entry context is sent again; the runtime
 * writes it only where it differs, so an unchanged record costs one request and nothing else.
 *
 * Never throws and never stops the session opening: an about that could not be updated leaves
 * the one it had, and the conversation is still the right one. Resolves whether it was sent.
 */
export async function keepAboutCurrent(
  client: { setSessionAbout(sessionId: string, about: Record<string, string>): Promise<unknown> },
  sessionId: string,
  entry: Readonly<Record<string, string>>,
): Promise<boolean> {
  return (await updateAbout(client, sessionId, entry)).sent;
}

/**
 * `keepAboutCurrent`, and what the session was about before it, where the server said
 * (ADR 0112): `previous` is how a project's conversation tells a run it is being told of for
 * the first time from one it already knew. Never throws.
 */
export async function updateAbout(
  client: { setSessionAbout(sessionId: string, about: Record<string, string>): Promise<unknown> },
  sessionId: string,
  entry: Readonly<Record<string, string>>,
): Promise<{ sent: boolean; previous?: Record<string, string> }> {
  const { about } = sessionAboutRecord(entry, '');
  if (!about) return { sent: false };
  try {
    const answer = await client.setSessionAbout(sessionId, about) as { previous?: unknown } | undefined;
    const previous = answer?.previous;
    return {
      sent: true,
      ...(previous && typeof previous === 'object' && !Array.isArray(previous)
        ? { previous: previous as Record<string, string> } : {}),
    };
  } catch {
    return { sent: false };
  }
}

/**
 * The project work to start in a session reopened by its stored id, told the entry context
 * again: `updateAbout` and `projectWorkFor` together, for the frame's reopen path, which tells
 * the about on its own rather than through `openSessionAboutRecord`.
 */
export async function keepAboutCurrentForWork(
  client: { setSessionAbout(sessionId: string, about: Record<string, string>): Promise<unknown> },
  sessionId: string,
  entry: Readonly<Record<string, string>>,
  /** The session's model, where the frame knows it, for the brief (ADR 0118). */
  model?: string,
): Promise<ProjectWork | undefined> {
  const told = await updateAbout(client, sessionId, entry);
  return told.sent ? projectWorkFor(entry, told.previous, told.previous !== undefined, model) : undefined;
}

/**
 * What reopening a record's session did about its code, for the host to show.
 *
 * `attached` is the workspace a session with no code was given first and `also` the ones opened
 * beside it — or, for a session that already had code, every one added beside that. `readOnly`
 * is a single repository's Read-only, where keeping it changed something. `unattached` and
 * `unsettled` are sentences, one line for each repository they are about.
 */
export interface Reopened {
  attached?: string;
  also?: string[];
  unattached?: string;
  /** The first reason in `unattached` that is about attaching code, by case. See `AttachTroubleAt`. */
  trouble?: AttachTroubleAt;
  readOnly?: boolean;
  unsettled?: string;
}

/**
 * Give a session that already exists the code its record names, where it has none yet.
 *
 * A record's session is made once and kept: the host stores its id and reopens it. One made
 * before the record named a repository — or before a panel attached one at all — reopened
 * without its code and stayed that way, because only creating attached it. This is the reopening
 * half: the repository resolves exactly as it does for a create, and is attached to the session
 * that is already there.
 *
 * **With one repository, only to a session with no code, as it always was.** A session that
 * has any code — the record's, attached when it was made or on an earlier reopen, or one somebody
 * chose in the conversation — is not attached to, and nothing is ever detached. That was forced
 * when the runtime could only raise a conversation onto its first code; it is a choice now that
 * code can be added beside code, and kept on purpose: a record with one repository is promised
 * the path it took before records could have several, so linking a list changes nothing for
 * anybody who has not linked one. The cost is that a conversation working in other code is not
 * given its one-repository record's, which it never was.
 *
 * **With several, the ones it is missing are added beside the ones it has** — see
 * `attachSeveral`. That is what a record linking a second repository after its conversation got
 * the first needs, and it is told apart without asking the git host on every open.
 *
 * **A session with code is kept to the record's Read-only setting** — see `keepReadOnly`. That is
 * the one thing about its code a record can change after attaching it that the conversation
 * could act against, and it is asked without the git host.
 *
 * **Nothing is said to the conversation from here.** The runtime already says it: an attach
 * shows "Opening …" as a step, and puts a note in the conversation that the code is now there
 * and that anything concluded before it was reasoned without it — the same note every attach
 * gets, whoever asked. A message from the frame would duplicate that and start a turn nobody
 * asked for, spending on an answer to a question nobody put.
 *
 * **Never throws, and never costs the conversation.** The session is already open when this
 * runs. A repository that cannot be found or attached leaves it open as it was, and `unattached`
 * says why, in the same words a create would use, for the host to show; a Read-only setting
 * that could not be kept is said in `unsettled`, the same way.
 */
export async function attachRecordRepository(
  client: RecordReopenClient,
  session: SessionCode,
  entry: Readonly<Record<string, string>>,
): Promise<Reopened> {
  const { repositories, unread } = recordRepositories(entry);
  // Finished for good: the runtime refuses to attach to it and the door refuses every request
  // its code makes, so a strip reporting either would answer something nobody asked for.
  if (session.state && CLOSED.has(session.state)) return {};
  const withUnread = (out: Reopened): Reopened => {
    const unattached = lines([out.unattached, unread]);
    const trouble = out.trouble ?? unreadSaid(unread)?.trouble;
    return { ...out, ...(unattached ? { unattached } : {}), ...(trouble ? { trouble } : {}) };
  };
  if (repositories.length > 1) return withUnread(await attachSeveral(client, session, repositories));

  const [only] = repositories;
  if (!only) return withUnread({});
  if ((session.tier ?? 0) >= 1) return withUnread(await keepReadOnly(client, session.id, only, only.readOnly));

  const found = await workspaceForRepository(client, only);
  if ('problem' in found) return withUnread(told([found.problem]));
  try {
    await client.attachWorkspace(session.id, found.workspaceId, only.readOnly ? { readOnly: true } : undefined);
    return withUnread({ attached: found.workspaceId });
  } catch (err) {
    const refused = serverSaid(err);
    return withUnread(told([about(only, `Opened without ${nameOf(only)}. ${refused.words}`, refused.case)]));
  }
}

/**
 * A reopened session and a record with several repositories: what it has is kept to the record's
 * Read-only, and what it is missing is added beside.
 *
 * **Which it has is read off Chat42's own list, not asked of the git host.** The session names
 * its code by workspace id, the record by address; the person's workspaces carry both, a
 * repository's remote and a folder's path, so one call to Chat42's server matches every one.
 * Only a repository the session does not have is checked, which registers it as a create would —
 * so the git host is asked once for each repository the record gains, not on every open.
 *
 * A session with no code has its first attached, as the single path would, and the rest beside
 * it. One that has code keeps it, primary and all: a repository the record links that is not
 * there is added beside whatever is, and nothing is replaced or detached. A list that could not
 * be had leaves the session as it was and says so, rather than adding what it may already hold.
 */
async function attachSeveral(
  client: RecordReopenClient,
  session: SessionCode,
  repositories: readonly RecordRepository[],
): Promise<Reopened> {
  const out = (attached: readonly string[], problems: readonly Said[], unsettled: readonly string[] = []) => {
    const [first, ...rest] = attached;
    const notKept = lines(unsettled);
    return {
      ...(first && (session.tier ?? 0) < 1 ? { attached: first, ...(rest.length ? { also: rest } : {}) } : {}),
      ...(first && (session.tier ?? 0) >= 1 ? { also: [...attached] } : {}),
      ...told(problems),
      ...(notKept ? { unsettled: notKept } : {}),
    };
  };

  if ((session.tier ?? 0) < 1) {
    const { found, problems } = await resolveAll(client, repositories);
    const each = await attachEach(client, session.id, found, false);
    return out(each.attached, [...problems, ...each.problems]);
  }

  let rows: ReadonlyArray<WorkspaceRow>;
  try {
    rows = (await client.workspaces()).workspaces;
  } catch (err) {
    // The setting is still kept: it needs only each remote, and the door changes nothing for one
    // the session does not hold. Without it a repository ticked Read-only since went on pushing
    // whenever the list failed, the one failure the setting exists to prevent.
    const unsettled: string[] = [];
    for (const repo of repositories) {
      const kept = await keepReadOnly(client, session.id, repo, repo.readOnly);
      if (kept.unsettled) unsettled.push(kept.unsettled);
    }
    return out([], [{
      line: `Could not tell which of this record's repositories this conversation already has, so none was added. `
        + `${err instanceof Error ? err.message : String(err)}`,
      trouble: { case: 'other' },
    }], unsettled);
  }
  const held = new Set([session.repoId, ...(session.alsoRepoIds ?? [])].filter((id): id is string => Boolean(id)));
  const has = (repo: RecordRepository): boolean => (FOLDER.test(repo.url)
    ? rows.some((w) => w.kind === 'folder' && held.has(w.id) && Boolean(w.path) && sameFolder(repo.url, w.path!))
    : rows.some((w) => w.kind === 'repo' && held.has(w.id) && sameRepository(repo.url, w.remote ?? '')));

  const unsettled: string[] = [];
  const missing: RecordRepository[] = [];
  for (const repo of repositories) {
    if (!has(repo)) {
      missing.push(repo);
      continue;
    }
    const kept = await keepReadOnly(client, session.id, repo, repo.readOnly);
    if (kept.unsettled) unsettled.push(kept.unsettled);
  }
  if (!missing.length) return out([], [], unsettled);
  const { found, problems } = await resolveAll(client, missing, rows);
  const each = await attachEach(client, session.id, found, true);
  return out(each.attached, [...problems, ...each.problems], unsettled);
}

/**
 * The record's Read-only setting, kept by a session that already has code.
 *
 * Read-only is set on the git door's credential when code is attached, and a record's session
 * is attached once. So a session whose record was ticked Read-only after its code arrived went
 * on pushing, while the person who ticked it had been told it could not; and one unticked went
 * on being refused. The door changes the credential the container already holds
 * (`GitDoor.setReadOnly`), so this asks on every reopen of a session with code: one write on
 * Chat42's own server, no git host, nothing reopened, and nothing changed where the setting
 * already holds or where the session's code is another repository than the record's.
 *
 * Both ways, because both are stale: a session the record says may push, refused at the door,
 * is wrong in the same way as one the record says may not, pushing. A folder is left out: a
 * paired machine pushes with its own git, which never goes through the door.
 *
 * Never throws. A setting that could not be kept is said, in `unsettled`, for the host to show
 * — above all one that left a conversation able to push after the person was told it could not.
 */
async function keepReadOnly(
  client: RecordReopenClient,
  sessionId: string,
  repo: Pick<RecordRepository, 'url' | 'label'>,
  readOnly: boolean,
): Promise<{ readOnly?: boolean; unsettled?: string }> {
  if (FOLDER.test(repo.url)) return {};
  const name = nameOf(repo);
  try {
    const out = await client.setWorkspaceReadOnly(sessionId, { remote: repo.url, readOnly });
    if (out.refused) {
      return {
        unsettled: `${name} stays read-only in this conversation. It pushes with the sign-in of the `
          + 'person it belongs to, and only they can let it push.',
      };
    }
    return out.changed ? { readOnly } : {};
  } catch (err) {
    return {
      unsettled: readOnly
        ? `This conversation may still be able to push to ${name}: it could not be made read-only. ${inServerWords(err)}`
        : `This conversation still cannot push to ${name}. ${inServerWords(err)}`,
    };
  }
}

/**
 * Whether the record's repository is to be attached read-only.
 *
 * Read-only unless the host says otherwise, in words: the record's own default, and the safe
 * one — PRD analysis reads, and a session able to push before anybody decided it should is the
 * risk. Chat42's git door enforces it; this only asks for it.
 */
export function readOnlyFor(entry: Readonly<Record<string, string>>): boolean {
  return entry['repositoryReadOnly'] !== 'false';
}

/**
 * The facts that name the organisation a record belongs to, as rule42-cloud sends them in its
 * entry context (ADR 0101): `orgId`, which the runtime reads to choose the organisation's
 * library and which Rule42's tools are called in, and `orgName`, which is for the model.
 */
export const ORGANISATION_KEYS = ['orgId', 'orgName'] as const;

/**
 * The create input for a session about the record a host has on screen.
 *
 * Entry context is passed through as `about` as the host wrote it — the runtime
 * bounds it and says it to the model as context, not instruction. An empty entry
 * context makes an ordinary named session, and a kind this does not know gets
 * `about` without a focus, which is how a host that is not Rule42 is served.
 *
 * One value is said in words rather than as written: `repositories`, a JSON list.
 * The runtime keeps 300 characters of a value, so the JSON of three repositories
 * with long addresses is cut mid-object, and the model would read a list broken
 * off inside a quoted string. As one line — each by its label, its address, and whether it is primary
 * or read-only — the same 300 hold most lists whole, and one that is cut is cut
 * between readable entries. The whole list is still the record's, read through
 * its own tools (`uow.fields.repositories`, ADR 0087). A list that cannot be read
 * is not a fact, as a blank value is not, and is left out.
 */
export function sessionAboutRecord(
  entry: Readonly<Record<string, string>>,
  title: string,
): RecordSessionInput {
  const about: Record<string, string> = {};
  // The organisation first, so the runtime's bound on how many facts it keeps (`aboutOf`)
  // never cuts it: a host sends it last, after the record, and it is the one fact that
  // decides which organisation's library and tools the conversation uses (ADR 0101).
  for (const key of ORGANISATION_KEYS) {
    const value = entry[key];
    if (typeof value === 'string' && value.trim()) about[key] = value;
  }
  for (const [key, value] of Object.entries(entry)) {
    if (key in about) continue;
    if (typeof value === 'string' && value.trim()) about[key] = value;
  }
  if (about['repositories'] !== undefined) {
    const { repositories, listed } = recordRepositories(entry);
    if (listed) {
      about['repositories'] = repositories.map((r) => {
        const notes = [r.primary ? 'primary' : '', r.readOnly ? 'read-only' : ''].filter(Boolean);
        return `${r.label ? `${r.label}: ` : ''}${r.url}${notes.length ? ` (${notes.join(', ')})` : ''}`;
      }).join('; ');
    } else {
      delete about['repositories'];
    }
  }
  // What the host said in one word, said in a sentence, for the two scopes whose job is not
  // what their record's kind alone would suggest (ADR 0097).
  if (isDelivery(entry)) about['scope'] = DELIVERY_SCOPE;
  if (isProject(entry)) projectAbout(entry, about);
  if (isIdeas(entry) || isProjectPrd(entry) || isGeneral(entry)) {
    // Ideas have no code, nor has Arthur's general conversation (ADR 0141). A PRD in a project has no
    // repository attached (ADR 0136): its code is the project's, read through Rule42's tools. Either
    // way a repository is not a fact here.
    about['scope'] = isIdeas(entry) ? IDEAS_SCOPE : isGeneral(entry) ? ARTHUR_SCOPE : projectPrdScope(entry['id'] ?? '');
    delete about['repository'];
    delete about['repositories'];
    delete about['repositoryReadOnly'];
  }
  const kind = about['kind'];
  const focus = isDelivery(entry)
    ? DELIVERY_FOCUS
    : kind !== undefined && Object.hasOwn(RECORD_TOOLSETS, kind) ? RECORD_TOOLSETS[kind] : undefined;
  return {
    ...(title ? { title } : {}),
    ...(Object.keys(about).length ? { about } : {}),
    ...(focus ? { toolsets: { [RULE42_CONNECTOR]: focus } } : {}),
  };
}
